import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SimpleUser, defaultSessionDescriptionHandlerFactory, type SimpleUserOptions,
} from 'sip.js/lib/platform/web/index.js';

export type PhoneState =
  | 'offline' | 'connecting' | 'ready' | 'ringing'
  | 'calling' | 'connected' | 'held' | 'failed';

export interface SipSessionConfiguration {
  websocketUrl: string;
  aor: string;
  authorizationUsername: string;
  authorizationPassword: string;
  sipDomain: string;
  expiresAt: string;
  iceServers: RTCIceServer[];
  linphone?: {
    serverUri: string;
    identityUri: string;
    authorizationUsername: string;
    authorizationPassword: string;
    mediaEncryption: 'srtp';
    verifyServerCertificate: true;
    clientCertificateRequired: true;
  };
}

export interface PhoneDiagnostic {
  phase: 'connect_started' | 'websocket_connected' | 'register_sent' | 'registered'
    | 'session_created' | 'invite_sent' | 'sip_trying' | 'sip_progress'
    | 'sip_accepted' | 'sip_rejected' | 'session_terminated'
    | 'media_playback_started' | 'media_playback_failed'
    | 'media_flowing' | 'media_flow_failed' | 'media_summary'
    | 'transport_disconnected' | 'operation_failed';
  callId?: string;
  sipStatus?: number;
  sipReason?: string;
  sessionState?: string;
  transportState?: string;
  connectionState?: string;
  iceConnectionState?: string;
  signalingState?: string;
  playbackState?: string;
  localCandidateType?: string;
  remoteCandidateType?: string;
  relayProtocol?: string;
  bytesReceived?: number;
  bytesSent?: number;
  packetsReceived?: number;
  packetsSent?: number;
  packetsLost?: number;
  jitterMs?: number;
  errorCode?: string;
  elapsedMs: number;
}

export type MediaPathState = 'idle' | 'armed' | 'negotiating' | 'active' | 'blocked' | 'no_media';

export interface MediaPathEvidence {
  state: MediaPathState;
  packetsReceived: number;
  packetsSent: number;
  localCandidateType: string;
  remoteCandidateType: string;
  relayProtocol: string;
}

export interface AudioDeviceOption {
  deviceId: string;
  label: string;
}

type DiagnosticSink = (event: PhoneDiagnostic) => void | Promise<void>;

type AudioUnlock = { context: AudioContext; stream: MediaStream };

type LocalTone = { stop: () => Promise<void> };

type SipSessionRuntime = {
  sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection };
};

type SimpleUserRuntime = { session?: SipSessionRuntime };

interface MediaStats {
  connectionState: string;
  iceConnectionState: string;
  signalingState: string;
  localCandidateType: string;
  remoteCandidateType: string;
  relayProtocol: string;
  bytesReceived: number;
  bytesSent: number;
  packetsReceived: number;
  packetsSent: number;
  packetsLost: number;
  jitterMs: number;
}

export function useSipPhone(onDiagnostic?: DiagnosticSink) {
  const phone = useRef<SimpleUser | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const diagnosticSink = useRef(onDiagnostic);
  const connectStartedAt = useRef(0);
  const callStartedAt = useRef(0);
  const activeCallId = useRef<string | null>(null);
  const callWasAccepted = useRef(false);
  const callTerminationPending = useRef(false);
  const lastSipFailure = useRef<{ status: number; reason: string } | null>(null);
  const hangupResolutionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputDeviceIdRef = useRef('');
  const outputDeviceIdRef = useRef('');
  const preparedMicrophone = useRef<Promise<MediaStream> | null>(null);
  const audioUnlock = useRef<AudioUnlock | null>(null);
  const ringback = useRef<Promise<LocalTone | null> | null>(null);
  const remotePlaybackAttempt = useRef<Promise<void> | null>(null);
  const mediaStatsTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMediaStats = useRef<MediaStats | null>(null);
  const [state, setState] = useState<PhoneState>('offline');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domain, setDomain] = useState('');
  const [audioInputs, setAudioInputs] = useState<AudioDeviceOption[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<AudioDeviceOption[]>([]);
  const [inputDeviceId, setInputDeviceIdState] = useState(inputDeviceIdRef.current);
  const [outputDeviceId, setOutputDeviceIdState] = useState(outputDeviceIdRef.current);
  const [inputLevel, setInputLevel] = useState(0);
  const [outputLevel, setOutputLevel] = useState(0);
  const [remoteSoundDetected, setRemoteSoundDetected] = useState(false);
  const [audioTest, setAudioTest] = useState<'idle' | 'microphone' | 'speaker'>('idle');
  const [mediaPath, setMediaPath] = useState<MediaPathEvidence>({
    state: 'idle', packetsReceived: 0, packetsSent: 0,
    localCandidateType: '', remoteCandidateType: '', relayProtocol: '',
  });
  const outputSelectionSupported = typeof HTMLMediaElement !== 'undefined'
    && 'setSinkId' in HTMLMediaElement.prototype;

  useEffect(() => { diagnosticSink.current = onDiagnostic; }, [onDiagnostic]);

  const emitDiagnostic = useCallback((event: Omit<PhoneDiagnostic, 'elapsedMs' | 'callId'>) => {
    const startedAt = activeCallId.current ? callStartedAt.current : connectStartedAt.current;
    const payload: PhoneDiagnostic = {
      ...event,
      ...(activeCallId.current ? { callId: activeCallId.current } : {}),
      elapsedMs: startedAt ? Math.max(0, Math.round(performance.now() - startedAt)) : 0,
    };
    void Promise.resolve(diagnosticSink.current?.(payload)).catch(() => undefined);
  }, []);

  const stopRingback = useCallback(() => {
    const pending = ringback.current;
    ringback.current = null;
    void pending?.then((tone) => tone?.stop()).catch(() => undefined);
  }, []);

  const fail = useCallback((cause: unknown) => {
    stopRingback();
    setState('failed');
    setError(phoneErrorMessage(cause));
    emitDiagnostic({ phase: 'operation_failed', errorCode: phoneErrorCode(cause) });
  }, [emitDiagnostic, stopRingback]);

  const releaseAudioUnlock = useCallback(() => {
    const active = audioUnlock.current;
    audioUnlock.current = null;
    if (!active) return;
    active.stream.getTracks().forEach((track) => track.stop());
    void active.context.close().catch(() => undefined);
  }, []);

  const releasePreparedMicrophone = useCallback(() => {
    const pending = preparedMicrophone.current;
    preparedMicrophone.current = null;
    if (!pending) return;
    void pending.then((stream) => {
      stream.getTracks().forEach((track) => track.stop());
    }).catch(() => undefined);
  }, []);

  const stopMediaMonitor = useCallback(() => {
    if (mediaStatsTimer.current) clearInterval(mediaStatsTimer.current);
    mediaStatsTimer.current = null;
  }, []);

  const preparePlayback = useCallback(() => {
    const element = remoteAudioRef.current;
    if (!element) return;
    element.autoplay = true;
    element.setAttribute('playsinline', '');
    element.muted = false;
    element.volume = 1;
    releaseAudioUnlock();
    if (typeof AudioContext === 'undefined') {
      void element.play().catch(() => undefined);
      return;
    }
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const destination = context.createMediaStreamDestination();
    gain.gain.value = 0;
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start();
    element.srcObject = destination.stream;
    audioUnlock.current = { context, stream: destination.stream };
    setMediaPath((current) => ({ ...current, state: 'armed' }));
    const resume = context.resume();
    const playback = element.play();
    void Promise.all([resume, playback]).catch((cause) => {
      setMediaPath((current) => ({ ...current, state: 'blocked' }));
      setError(audioPlaybackError(cause));
    });
  }, [releaseAudioUnlock]);

  const prepareCallMedia = useCallback(() => {
    preparePlayback();
    releasePreparedMicrophone();
    if (!navigator.mediaDevices?.getUserMedia) return;
    const pending = navigator.mediaDevices.getUserMedia({
      audio: selectedMicrophoneConstraint(inputDeviceIdRef.current), video: false,
    });
    preparedMicrophone.current = pending;
    void pending.catch((cause) => {
      if (preparedMicrophone.current !== pending) return;
      setError(phoneErrorMessage(cause));
    });
  }, [preparePlayback, releasePreparedMicrophone]);

  const cancelPreparedMedia = useCallback(() => {
    releasePreparedMicrophone();
    releaseAudioUnlock();
  }, [releaseAudioUnlock, releasePreparedMicrophone]);

  const startMediaMonitor = useCallback((client: SimpleUser) => {
    stopMediaMonitor();
    const startedAt = performance.now();
    let flowReported = false;
    let failureReported = false;
    const inspect = async () => {
      const peer = peerConnection(client);
      if (!peer) return;
      const stats = await readMediaStats(peer);
      lastMediaStats.current = stats;
      setMediaPath({
        state: stats.packetsReceived > 0 && stats.packetsSent > 0 ? 'active'
          : performance.now() - startedAt >= 6_000 ? 'no_media' : 'negotiating',
        packetsReceived: stats.packetsReceived,
        packetsSent: stats.packetsSent,
        localCandidateType: stats.localCandidateType,
        remoteCandidateType: stats.remoteCandidateType,
        relayProtocol: stats.relayProtocol,
      });
      if (!flowReported && stats.packetsReceived > 0 && stats.packetsSent > 0) {
        flowReported = true;
        emitDiagnostic({ phase: 'media_flowing', playbackState: 'playing', ...stats });
      }
      if (!failureReported && performance.now() - startedAt >= 6_000
          && (stats.packetsReceived === 0 || stats.packetsSent === 0)) {
        failureReported = true;
        const errorCode = stats.packetsReceived === 0 && stats.packetsSent === 0
          ? 'WEBRTC_NO_MEDIA' : stats.packetsReceived === 0
            ? 'WEBRTC_NO_INBOUND_MEDIA' : 'WEBRTC_NO_OUTBOUND_MEDIA';
        emitDiagnostic({ phase: 'media_flow_failed', playbackState: 'playing', errorCode, ...stats });
        emitDiagnostic({
          phase: 'operation_failed', errorCode,
          sessionState: `media rx_packets:${stats.packetsReceived} tx_packets:${stats.packetsSent}`,
          connectionState: stats.connectionState,
          iceConnectionState: stats.iceConnectionState,
          signalingState: stats.signalingState,
        });
        setError(stats.packetsReceived === 0
          ? 'Call connected, but no caller audio reached this browser. Open Audio devices and reconnect the phone.'
          : 'Call connected, but the microphone sent no audio. Open Audio devices and reconnect the phone.');
      }
    };
    void inspect().catch(() => undefined);
    mediaStatsTimer.current = setInterval(() => { void inspect().catch(() => undefined); }, 1_000);
  }, [emitDiagnostic, stopMediaMonitor]);

  const ensureRemoteAudioPlayback = useCallback((client: SimpleUser) => {
    if (remotePlaybackAttempt.current) return remotePlaybackAttempt.current;
    const attempt = (async () => {
      const element = remoteAudioRef.current;
      if (!element) throw new Error('The speaker output is not ready.');
      let stream = client.remoteMediaStream;
      for (let retries = 0; !stream && retries < 20; retries += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 100));
        stream = client.remoteMediaStream;
      }
      if (!stream?.getAudioTracks().length) throw new Error('The call has no remote audio track.');
      element.autoplay = true;
      element.setAttribute('playsinline', '');
      element.muted = false;
      element.volume = 1;
      element.srcObject = stream;
      await applyAudioOutput(element, outputDeviceIdRef.current);
      await element.play();
      releaseAudioUnlock();
      setError(null);
      setMediaPath((current) => ({ ...current, state: 'negotiating' }));
      const peer = peerConnection(client);
      emitDiagnostic({
        phase: 'media_playback_started', playbackState: 'playing',
        connectionState: peer?.connectionState ?? '',
        iceConnectionState: peer?.iceConnectionState ?? '',
        signalingState: peer?.signalingState ?? '',
      });
      startMediaMonitor(client);
    })().catch((cause) => {
      releaseAudioUnlock();
      setMediaPath((current) => ({ ...current, state: 'blocked' }));
      setError(audioPlaybackError(cause));
      const peer = peerConnection(client);
      emitDiagnostic({
        phase: 'media_playback_failed', playbackState: 'blocked',
        connectionState: peer?.connectionState ?? '',
        iceConnectionState: peer?.iceConnectionState ?? '',
        signalingState: peer?.signalingState ?? '',
        errorCode: 'REMOTE_AUDIO_PLAYBACK_FAILED',
      });
      emitDiagnostic({
        phase: 'operation_failed', sessionState: 'media playback blocked',
        connectionState: peer?.connectionState ?? '',
        iceConnectionState: peer?.iceConnectionState ?? '',
        signalingState: peer?.signalingState ?? '',
        errorCode: 'REMOTE_AUDIO_PLAYBACK_FAILED',
      });
    });
    remotePlaybackAttempt.current = attempt;
    return attempt;
  }, [emitDiagnostic, releaseAudioUnlock, startMediaMonitor]);

  const refreshAudioDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = deviceOptions(devices, 'audioinput', 'Microphone');
    const outputs = deviceOptions(devices, 'audiooutput', 'Speaker');
    const nextInput = validDeviceId(inputDeviceIdRef.current, inputs);
    const nextOutput = validDeviceId(outputDeviceIdRef.current, outputs);
    inputDeviceIdRef.current = nextInput;
    outputDeviceIdRef.current = nextOutput;
    setAudioInputs(inputs);
    setAudioOutputs(outputs);
    setInputDeviceIdState(nextInput);
    setOutputDeviceIdState(nextOutput);
    if (remoteAudioRef.current) await applyAudioOutput(remoteAudioRef.current, nextOutput);
  }, []);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    void refreshAudioDevices().catch(() => undefined);
    const changed = () => { void refreshAudioDevices().catch(() => undefined); };
    navigator.mediaDevices.addEventListener?.('devicechange', changed);
    return () => navigator.mediaDevices.removeEventListener?.('devicechange', changed);
  }, [refreshAudioDevices]);

  const connect = useCallback(async (
    configuration: SipSessionConfiguration, replaceCredentials = false,
  ) => {
    if (phone.current?.isConnected() && !replaceCredentials) {
      setError(null);
      setState('ready');
      return;
    }
    if (!remoteAudioRef.current) throw new Error('The audio output is not ready.');
    setError(null);
    setState('connecting');
    setDomain(configuration.sipDomain);
    connectStartedAt.current = performance.now();
    activeCallId.current = null;
    callWasAccepted.current = false;
    callTerminationPending.current = false;
    lastSipFailure.current = null;
    remotePlaybackAttempt.current = null;
    stopRingback();
    stopMediaMonitor();
    lastMediaStats.current = null;
    setMediaPath((current) => ({ ...current, state: 'idle', packetsReceived: 0, packetsSent: 0 }));
    if (hangupResolutionTimer.current) {
      clearTimeout(hangupResolutionTimer.current);
      hangupResolutionTimer.current = null;
    }
    emitDiagnostic({ phase: 'connect_started', transportState: 'connecting' });

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser does not provide microphone access.');
      }
      const microphone = await navigator.mediaDevices.getUserMedia({
        audio: selectedMicrophoneConstraint(inputDeviceIdRef.current), video: false,
      });
      microphone.getTracks().forEach((track) => track.stop());
      await refreshAudioDevices();
    } catch (cause) {
      fail(cause);
      throw cause;
    }

    if (phone.current?.isConnected()) {
      await phone.current.unregister().catch(() => undefined);
      await phone.current.disconnect();
      phone.current = null;
    }

    const options: SimpleUserOptions = {
      aor: configuration.aor,
      reconnectionAttempts: 3,
      reconnectionDelay: 4,
      media: {
        constraints: { audio: true, video: false },
        remote: { audio: remoteAudioRef.current },
      },
      userAgentOptions: {
        authorizationUsername: configuration.authorizationUsername,
        authorizationPassword: configuration.authorizationPassword,
        displayName: 'Veritas Call Center',
        sessionDescriptionHandlerFactory: defaultSessionDescriptionHandlerFactory(async (constraints) => {
          const pending = preparedMicrophone.current;
          preparedMicrophone.current = null;
          if (pending) {
            const stream = await pending;
            if (stream.getAudioTracks().some((track) => track.readyState === 'live')) return stream;
            stream.getTracks().forEach((track) => track.stop());
          }
          return navigator.mediaDevices.getUserMedia(constraints);
        }),
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: configuration.iceServers,
            iceTransportPolicy: 'relay',
          },
        },
      },
      delegate: {
        onCallCreated: () => {
          emitDiagnostic({ phase: 'session_created', sessionState: 'initial' });
          setState((current) => current === 'ringing' ? current : 'calling');
        },
        onCallReceived: () => {
          callWasAccepted.current = false;
          callTerminationPending.current = false;
          lastSipFailure.current = null;
          setState('ringing');
        },
        onCallAnswered: () => {
          stopRingback();
          callWasAccepted.current = true;
          lastSipFailure.current = null;
          setError(null);
          if (!callTerminationPending.current) setState('connected');
          void ensureRemoteAudioPlayback(client);
        },
        onCallHangup: () => {
          stopRingback();
          const traceId = activeCallId.current;
          callTerminationPending.current = true;
          emitDiagnostic({ phase: 'session_terminated', sessionState: 'terminated' });
          if (lastMediaStats.current) {
            emitDiagnostic({
              phase: 'media_summary',
              playbackState: remoteAudioRef.current?.paused ? 'paused' : 'playing',
              ...lastMediaStats.current,
            });
          }
          stopMediaMonitor();
          releaseAudioUnlock();
          releasePreparedMicrophone();
          remotePlaybackAttempt.current = null;
          setMuted(false);
          if (hangupResolutionTimer.current) clearTimeout(hangupResolutionTimer.current);
          // SIP.js can deliver the accepted-response callback just after the
          // terminated-session callback. Allow that final response to settle
          // before classifying the call, and never invent a failure without a
          // negative SIP response.
          hangupResolutionTimer.current = setTimeout(() => {
            if (activeCallId.current !== traceId) return;
            const response = lastSipFailure.current;
            if (response && !callWasAccepted.current) {
              setError(`Call rejected with SIP ${response.status}${response.reason ? ` ${response.reason}` : ''}. Trace ${traceId ?? 'not assigned'}.`);
              setState('failed');
            } else {
              setError(null);
              setState('ready');
            }
            activeCallId.current = null;
            callWasAccepted.current = false;
            callTerminationPending.current = false;
            lastSipFailure.current = null;
            hangupResolutionTimer.current = null;
          }, 750);
        },
        onCallHold: (held) => setState(held ? 'held' : 'connected'),
        onRegistered: () => {
          emitDiagnostic({ phase: 'registered', transportState: 'registered' });
          setState('ready');
        },
        onServerConnect: () => emitDiagnostic({
          phase: 'websocket_connected', transportState: 'connected',
        }),
        onServerDisconnect: (cause) => {
          if (phone.current !== client) return;
          stopRingback();
          emitDiagnostic({ phase: 'transport_disconnected', transportState: 'disconnected' });
          setState('offline');
          setError(phoneErrorMessage(cause ?? new Error('The phone connection closed. Select Connect to restore it.')));
        },
      },
    };

    const client = new SimpleUser(configuration.websocketUrl, options);
    phone.current = client;
    try {
      await client.connect();
      emitDiagnostic({ phase: 'register_sent', transportState: 'connected' });
      await client.register();
    } catch (cause) {
      phone.current = null;
      fail(cause);
      throw cause;
    }
  }, [ensureRemoteAudioPlayback, fail, refreshAudioDevices, releaseAudioUnlock,
    releasePreparedMicrophone, stopMediaMonitor, stopRingback]);

  const disconnect = useCallback(async () => {
    const client = phone.current;
    phone.current = null;
    if (hangupResolutionTimer.current) {
      clearTimeout(hangupResolutionTimer.current);
      hangupResolutionTimer.current = null;
    }
    activeCallId.current = null;
    callWasAccepted.current = false;
    callTerminationPending.current = false;
    lastSipFailure.current = null;
    remotePlaybackAttempt.current = null;
    stopRingback();
    stopMediaMonitor();
    releaseAudioUnlock();
    releasePreparedMicrophone();
    lastMediaStats.current = null;
    setMediaPath({ state: 'idle', packetsReceived: 0, packetsSent: 0,
      localCandidateType: '', remoteCandidateType: '', relayProtocol: '' });
    if (!client) { setState('offline'); return; }
    try {
      if (client.isConnected()) {
        await client.unregister().catch(() => undefined);
        await client.disconnect();
      }
    } finally {
      setState('offline');
      setMuted(false);
    }
  }, [releaseAudioUnlock, releasePreparedMicrophone, stopMediaMonitor, stopRingback]);

  const call = useCallback(async (
    target: string, correlation: { callId: string; providerToken: string },
  ) => {
    const client = phone.current;
    if (!client?.isConnected() || !domain) throw new Error('Connect the phone before placing a call.');
    if (!/^[0-9a-f-]{36}$/i.test(correlation.callId)
        || !/^[0-9a-f]{64}$/i.test(correlation.providerToken)) {
      throw new Error('The call correlation token is invalid.');
    }
    setError(null);
    setState('calling');
    activeCallId.current = correlation.callId;
    callStartedAt.current = performance.now();
    callWasAccepted.current = false;
    callTerminationPending.current = false;
    lastSipFailure.current = null;
    remotePlaybackAttempt.current = null;
    stopRingback();
    ringback.current = startLocalRingback(outputDeviceIdRef.current)
      .catch(() => null);
    stopMediaMonitor();
    lastMediaStats.current = null;
    setMediaPath((current) => ({ ...current, state: 'negotiating', packetsReceived: 0, packetsSent: 0 }));
    if (hangupResolutionTimer.current) {
      clearTimeout(hangupResolutionTimer.current);
      hangupResolutionTimer.current = null;
    }
    try {
      await client.call(
        `sip:${target}@${domain}`,
        {
          extraHeaders: [
            `X-Veritas-Call-ID: ${correlation.callId}`,
            `X-Veritas-Call-Token: ${correlation.providerToken}`,
          ],
          sessionDescriptionHandlerOptions: {
            constraints: {
              audio: selectedMicrophoneConstraint(inputDeviceIdRef.current), video: false,
            },
          },
        },
        { requestDelegate: {
          onTrying: (response) => emitDiagnostic({
            phase: 'sip_trying', ...sipResponseDetails(response),
          }),
          onProgress: (response) => emitDiagnostic({
            phase: 'sip_progress', ...sipResponseDetails(response),
          }),
          onAccept: (response) => {
            if (activeCallId.current !== correlation.callId) return;
            stopRingback();
            callWasAccepted.current = true;
            lastSipFailure.current = null;
            setError(null);
            if (!callTerminationPending.current) setState('connected');
            emitDiagnostic({ phase: 'sip_accepted', ...sipResponseDetails(response) });
            void ensureRemoteAudioPlayback(client);
          },
          onRedirect: (response) => {
            if (activeCallId.current !== correlation.callId) return;
            stopRingback();
            const details = sipResponseDetails(response);
            lastSipFailure.current = { status: details.sipStatus ?? 300, reason: details.sipReason ?? '' };
            emitDiagnostic({ phase: 'sip_rejected', ...details });
          },
          onReject: (response) => {
            if (activeCallId.current !== correlation.callId) return;
            stopRingback();
            const details = sipResponseDetails(response);
            lastSipFailure.current = { status: details.sipStatus ?? 500, reason: details.sipReason ?? '' };
            emitDiagnostic({ phase: 'sip_rejected', ...details });
          },
        } },
      );
      emitDiagnostic({ phase: 'invite_sent', sessionState: 'establishing' });
    } catch (cause) { fail(cause); throw cause; }
  }, [domain, ensureRemoteAudioPlayback, fail, stopMediaMonitor, stopRingback]);

  const answer = useCallback(async () => {
    try {
      await phone.current?.answer({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: selectedMicrophoneConstraint(inputDeviceIdRef.current), video: false,
          },
        },
      });
    } catch (cause) { fail(cause); throw cause; }
  }, [fail]);

  const decline = useCallback(async () => {
    try { await phone.current?.decline(); } catch (cause) { fail(cause); throw cause; }
  }, [fail]);

  const hangup = useCallback(async () => {
    try { await phone.current?.hangup(); } catch (cause) { fail(cause); throw cause; }
  }, [fail]);

  const toggleHold = useCallback(async () => {
    const client = phone.current;
    if (!client) return;
    try { client.isHeld() ? await client.unhold() : await client.hold(); }
    catch (cause) { fail(cause); throw cause; }
  }, [fail]);

  const toggleMute = useCallback(() => {
    const client = phone.current;
    if (!client) return;
    if (client.isMuted()) client.unmute(); else client.mute();
    setMuted(client.isMuted());
  }, []);

  const sendTone = useCallback(async (tone: string) => {
    void playLocalKeyTone(tone, outputDeviceIdRef.current).catch(() => undefined);
    try { await phone.current?.sendDTMF(tone); } catch (cause) { fail(cause); throw cause; }
  }, [fail]);

  const playKeyTone = useCallback((tone: string) => {
    void playLocalKeyTone(tone, outputDeviceIdRef.current).catch(() => undefined);
  }, []);

  const selectInputDevice = useCallback((deviceId: string) => {
    const valid = validDeviceId(deviceId, audioInputs);
    inputDeviceIdRef.current = valid;
    setInputDeviceIdState(valid);
    setError(null);
  }, [audioInputs]);

  const selectOutputDevice = useCallback(async (deviceId: string) => {
    const valid = validDeviceId(deviceId, audioOutputs);
    if (!remoteAudioRef.current) throw new Error('The speaker output is not ready.');
    try {
      await applyAudioOutput(remoteAudioRef.current, valid);
      outputDeviceIdRef.current = valid;
      setOutputDeviceIdState(valid);
      setError(null);
    } catch (cause) {
      setError(audioOutputError(cause));
      throw cause;
    }
  }, [audioOutputs]);

  const testMicrophone = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    setAudioTest('microphone');
    setError(null);
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedMicrophoneConstraint(inputDeviceIdRef.current), video: false,
      });
      await measureStream(stream, setInputLevel, 3500);
      await refreshAudioDevices();
    } catch (cause) {
      setError(phoneErrorMessage(cause));
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
      setAudioTest('idle');
    }
  }, [refreshAudioDevices]);

  const testSpeaker = useCallback(async () => {
    setAudioTest('speaker');
    setError(null);
    try {
      await playSpeakerTest(outputDeviceIdRef.current, setOutputLevel);
      const client = phone.current;
      if (client?.remoteMediaStream) {
        remotePlaybackAttempt.current = null;
        await ensureRemoteAudioPlayback(client);
      }
    } catch (cause) {
      setError(audioOutputError(cause));
    } finally {
      setAudioTest('idle');
      window.setTimeout(() => setOutputLevel(0), 400);
    }
  }, [ensureRemoteAudioPlayback]);

  useEffect(() => {
    if (state !== 'connected' && state !== 'held') {
      setInputLevel(0);
      setOutputLevel(0);
      setRemoteSoundDetected(false);
      return;
    }
    let cancelled = false;
    let stop: (() => void) | null = null;
    let attempts = 0;
    const start = () => {
      if (cancelled) return;
      const local = phone.current?.localMediaStream;
      const remote = phone.current?.remoteMediaStream;
      if ((!local || !remote) && attempts < 12) {
        attempts += 1;
        window.setTimeout(start, 200);
        return;
      }
      if (!local || !remote) return;
      stop = monitorCallAudio(
        local, remote, setInputLevel, setOutputLevel, setRemoteSoundDetected,
      );
    };
    start();
    return () => {
      cancelled = true;
      stop?.();
      setInputLevel(0);
      setOutputLevel(0);
      setRemoteSoundDetected(false);
    };
  }, [state]);

  useEffect(() => () => {
    if (hangupResolutionTimer.current) clearTimeout(hangupResolutionTimer.current);
    stopMediaMonitor();
    releaseAudioUnlock();
    releasePreparedMicrophone();
    void disconnect();
  }, [disconnect, releaseAudioUnlock, releasePreparedMicrophone, stopMediaMonitor]);

  return {
    state, muted, error, remoteAudioRef, connect, disconnect, call,
    answer, decline, hangup, toggleHold, toggleMute, sendTone, playKeyTone,
    audioInputs, audioOutputs, inputDeviceId, outputDeviceId, inputLevel, outputLevel,
    remoteSoundDetected,
    outputSelectionSupported, audioTest, mediaPath, refreshAudioDevices,
    preparePlayback, prepareCallMedia, cancelPreparedMedia,
    selectInputDevice, selectOutputDevice, testMicrophone, testSpeaker,
  };
}

function deviceOptions(devices: MediaDeviceInfo[], kind: MediaDeviceKind, fallback: string) {
  let unnamed = 0;
  return devices.filter((device) => device.kind === kind).map((device) => {
    unnamed += 1;
    return { deviceId: device.deviceId, label: device.label || `${fallback} ${unnamed}` };
  });
}

function validDeviceId(preferred: string, devices: AudioDeviceOption[]) {
  if (preferred && devices.some((device) => device.deviceId === preferred)) return preferred;
  return devices.find((device) => device.deviceId === 'default')?.deviceId
    ?? devices[0]?.deviceId ?? '';
}

function selectedMicrophoneConstraint(deviceId: string): boolean | MediaTrackConstraints {
  return deviceId && deviceId !== 'default' ? { deviceId: { exact: deviceId } } : true;
}

async function applyAudioOutput(element: HTMLAudioElement, deviceId: string) {
  const sink = element as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
  if (!sink.setSinkId) {
    if (deviceId && deviceId !== 'default') {
      throw new Error('This browser follows the macOS sound output and cannot select a speaker here.');
    }
    return;
  }
  await sink.setSinkId(deviceId || 'default');
}

function audioOutputError(cause: unknown) {
  if (cause instanceof DOMException && cause.name === 'NotAllowedError') {
    return 'Speaker selection is blocked. Allow audio output access for x2f-planning.com.';
  }
  return cause instanceof Error ? cause.message : 'The selected speaker could not be used.';
}

function audioPlaybackError(cause: unknown) {
  if (cause instanceof DOMException && cause.name === 'NotAllowedError') {
    return 'The call connected, but the browser blocked speaker playback. Open Audio devices and select Test speaker to restore it.';
  }
  const reason = cause instanceof Error ? cause.message : '';
  return reason
    ? `The call connected, but speaker playback failed: ${reason}`
    : 'The call connected, but speaker playback failed. Open Audio devices, test the speaker, and reconnect.';
}

function peerConnection(client: SimpleUser): RTCPeerConnection | null {
  return (client as unknown as SimpleUserRuntime).session
    ?.sessionDescriptionHandler?.peerConnection ?? null;
}

async function readMediaStats(peer: RTCPeerConnection): Promise<MediaStats> {
  const report = await peer.getStats();
  let bytesReceived = 0;
  let bytesSent = 0;
  let packetsReceived = 0;
  let packetsSent = 0;
  let packetsLost = 0;
  let jitterMs = 0;
  let localCandidateType = '';
  let remoteCandidateType = '';
  let relayProtocol = '';
  let selectedPair: Record<string, unknown> | null = null;
  report.forEach((entry) => {
    const stat = entry as unknown as Record<string, unknown>;
    if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
      bytesReceived += finiteStat(stat.bytesReceived);
      packetsReceived += finiteStat(stat.packetsReceived);
      packetsLost += finiteStat(stat.packetsLost);
      jitterMs = Math.max(jitterMs, Math.round(nonnegativeStat(stat.jitter) * 1_000));
    }
    if (stat.type === 'outbound-rtp' && stat.kind === 'audio') {
      bytesSent += finiteStat(stat.bytesSent);
      packetsSent += finiteStat(stat.packetsSent);
    }
    if (stat.type === 'transport' && typeof stat.selectedCandidatePairId === 'string') {
      selectedPair = report.get(stat.selectedCandidatePairId) as unknown as Record<string, unknown>;
    }
    if (stat.type === 'candidate-pair' && stat.selected === true) selectedPair = stat;
  });
  const pair = selectedPair as Record<string, unknown> | null;
  if (pair) {
    const local = typeof pair.localCandidateId === 'string'
      ? report.get(pair.localCandidateId) as unknown as Record<string, unknown> : null;
    const remote = typeof pair.remoteCandidateId === 'string'
      ? report.get(pair.remoteCandidateId) as unknown as Record<string, unknown> : null;
    localCandidateType = safeStatText(local?.candidateType);
    remoteCandidateType = safeStatText(remote?.candidateType);
    relayProtocol = safeStatText(local?.relayProtocol ?? local?.protocol);
  }
  return {
    connectionState: safeStatText(peer.connectionState),
    iceConnectionState: safeStatText(peer.iceConnectionState),
    signalingState: safeStatText(peer.signalingState),
    localCandidateType, remoteCandidateType, relayProtocol,
    bytesReceived, bytesSent, packetsReceived, packetsSent, packetsLost, jitterMs,
  };
}

function finiteStat(value: unknown) {
  return Math.round(nonnegativeStat(value));
}

function nonnegativeStat(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

function safeStatText(value: unknown) {
  return typeof value === 'string' ? value.replace(/[^A-Za-z0-9_.:+/-]/g, '').slice(0, 32) : '';
}

function levelFromAnalyser(
  analyser: AnalyserNode,
  values: Uint8Array<ArrayBuffer>,
  sensitivity = 320,
) {
  analyser.getByteTimeDomainData(values);
  let sum = 0;
  let peak = 0;
  for (const value of values) {
    const sample = (value - 128) / 128;
    sum += sample * sample;
    peak = Math.max(peak, Math.abs(sample));
  }
  const rms = Math.sqrt(sum / values.length);
  return Math.min(100, Math.round(Math.max(rms * sensitivity, peak * sensitivity * 0.45)));
}

async function measureStream(stream: MediaStream, update: (level: number) => void, durationMs: number) {
  if (typeof AudioContext === 'undefined') return;
  const context = new AudioContext();
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);
  const values = new Uint8Array(new ArrayBuffer(analyser.fftSize));
  const started = performance.now();
  await context.resume().catch(() => undefined);
  await new Promise<void>((resolve) => {
    const sample = () => {
      update(levelFromAnalyser(analyser, values));
      if (performance.now() - started >= durationMs) { resolve(); return; }
      window.setTimeout(sample, 80);
    };
    sample();
  });
  source.disconnect();
  await context.close();
}

function monitorCallAudio(
  local: MediaStream,
  remote: MediaStream,
  updateInput: (level: number) => void,
  updateOutput: (level: number) => void,
  updateRemoteSound: (detected: boolean) => void,
) {
  if (typeof AudioContext === 'undefined') return () => undefined;
  const context = new AudioContext();
  const localSource = context.createMediaStreamSource(local);
  const remoteSource = context.createMediaStreamSource(remote);
  const localAnalyser = context.createAnalyser();
  const remoteAnalyser = context.createAnalyser();
  localAnalyser.fftSize = 256;
  remoteAnalyser.fftSize = 256;
  remoteAnalyser.smoothingTimeConstant = 0.15;
  localSource.connect(localAnalyser);
  remoteSource.connect(remoteAnalyser);
  const localValues = new Uint8Array(new ArrayBuffer(localAnalyser.fftSize));
  const remoteValues = new Uint8Array(new ArrayBuffer(remoteAnalyser.fftSize));
  let lastRemoteSoundAt = Number.NEGATIVE_INFINITY;
  void context.resume().catch(() => undefined);
  const timer = window.setInterval(() => {
    updateInput(levelFromAnalyser(localAnalyser, localValues));
    const remoteLevel = levelFromAnalyser(remoteAnalyser, remoteValues, 900);
    const now = performance.now();
    if (remoteLevel >= 1) lastRemoteSoundAt = now;
    updateOutput(remoteLevel);
    updateRemoteSound(now - lastRemoteSoundAt < 1_200);
  }, 100);
  return () => {
    window.clearInterval(timer);
    updateRemoteSound(false);
    localSource.disconnect();
    remoteSource.disconnect();
    void context.close();
  };
}

async function playSpeakerTest(deviceId: string, update: (level: number) => void) {
  if (typeof AudioContext === 'undefined') throw new Error('Speaker testing is unavailable in this browser.');
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const destination = context.createMediaStreamDestination();
  const audio = document.createElement('audio');
  audio.srcObject = destination.stream;
  audio.autoplay = true;
  oscillator.type = 'sine';
  oscillator.frequency.value = 660;
  gain.gain.setValueAtTime(0.0001, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.55);
  oscillator.connect(gain);
  gain.connect(destination);
  await applyAudioOutput(audio, deviceId);
  await context.resume();
  await audio.play();
  update(70);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.6);
  await new Promise((resolve) => window.setTimeout(resolve, 700));
  audio.pause();
  destination.stream.getTracks().forEach((track) => track.stop());
  await context.close();
}

async function startLocalRingback(deviceId: string): Promise<LocalTone> {
  if (typeof AudioContext === 'undefined') return { stop: async () => undefined };
  const context = new AudioContext();
  const low = context.createOscillator();
  const high = context.createOscillator();
  const gain = context.createGain();
  const destination = context.createMediaStreamDestination();
  const audio = document.createElement('audio');
  audio.srcObject = destination.stream;
  audio.autoplay = true;
  low.frequency.value = 440;
  high.frequency.value = 480;
  low.connect(gain);
  high.connect(gain);
  gain.connect(destination);
  const startedAt = context.currentTime;
  gain.gain.setValueAtTime(0.0001, startedAt);
  for (let offset = 0; offset < 180; offset += 6) {
    gain.gain.setValueAtTime(0.0001, startedAt + offset);
    gain.gain.exponentialRampToValueAtTime(0.055, startedAt + offset + 0.03);
    gain.gain.setValueAtTime(0.055, startedAt + offset + 1.95);
    gain.gain.exponentialRampToValueAtTime(0.0001, startedAt + offset + 2);
  }
  await applyAudioOutput(audio, deviceId);
  await context.resume();
  await audio.play();
  low.start();
  high.start();
  let stopped = false;
  return {
    stop: async () => {
      if (stopped) return;
      stopped = true;
      audio.pause();
      try { low.stop(); } catch {}
      try { high.stop(); } catch {}
      destination.stream.getTracks().forEach((track) => track.stop());
      await context.close().catch(() => undefined);
    },
  };
}

async function playLocalKeyTone(tone: string, deviceId: string): Promise<void> {
  const frequencies: Record<string, [number, number]> = {
    '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
    '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
    '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
    '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
  };
  const pair = frequencies[tone];
  if (!pair || typeof AudioContext === 'undefined') return;
  const context = new AudioContext();
  const first = context.createOscillator();
  const second = context.createOscillator();
  const gain = context.createGain();
  const destination = context.createMediaStreamDestination();
  const audio = document.createElement('audio');
  audio.srcObject = destination.stream;
  audio.autoplay = true;
  first.frequency.value = pair[0];
  second.frequency.value = pair[1];
  first.connect(gain);
  second.connect(gain);
  gain.connect(destination);
  gain.gain.setValueAtTime(0.0001, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.045, context.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.09);
  await applyAudioOutput(audio, deviceId);
  await context.resume();
  await audio.play();
  first.start();
  second.start();
  first.stop(context.currentTime + 0.1);
  second.stop(context.currentTime + 0.1);
  await new Promise((resolve) => window.setTimeout(resolve, 120));
  audio.pause();
  destination.stream.getTracks().forEach((track) => track.stop());
  await context.close();
}

function phoneErrorMessage(cause: unknown) {
  if (cause instanceof DOMException && ['NotAllowedError', 'SecurityError'].includes(cause.name)) {
    return 'Microphone access is blocked. Allow the microphone for x2f-planning.com, then reconnect.';
  }
  if (cause instanceof DOMException && cause.name === 'NotFoundError') {
    return 'No microphone was found. Connect a microphone or headset, then reconnect.';
  }
  return cause instanceof Error ? cause.message : 'The secure phone could not complete the request.';
}

function phoneErrorCode(cause: unknown): string {
  if (cause instanceof DOMException && ['NotAllowedError', 'SecurityError'].includes(cause.name)) {
    return 'MICROPHONE_PERMISSION_DENIED';
  }
  if (cause instanceof DOMException && cause.name === 'NotFoundError') return 'MICROPHONE_NOT_FOUND';
  if (cause instanceof DOMException && cause.name === 'NotReadableError') return 'MICROPHONE_NOT_READABLE';
  if (cause instanceof Error && /session already exists/i.test(cause.message)) return 'SESSION_ALREADY_EXISTS';
  if (cause instanceof Error && /connect the phone/i.test(cause.message)) return 'PHONE_NOT_CONNECTED';
  return 'SOFTPHONE_OPERATION_FAILED';
}

function sipResponseDetails(response: { message: { statusCode?: number; reasonPhrase?: string } }): {
  sipStatus?: number; sipReason?: string;
} {
  const status = response.message.statusCode;
  const reason = (response.message.reasonPhrase ?? '')
    .replace(/[^A-Za-z0-9_.: /+-]/g, '').trim().slice(0, 96);
  return {
    ...(status && status >= 100 && status <= 699 ? { sipStatus: status } : {}),
    ...(reason ? { sipReason: reason } : {}),
  };
}
